'use client';

import { useCallback } from 'react';
import useUser from '@alltrails/context/hooks/useUser';
import { addListItem, type ListsState, removeFromListItem, removeItemFromList } from '@alltrails/lists/slices/lists';
import type ListItem from '@alltrails/lists/types/ListItem';
import { useAppDispatch, useAppSelector } from '@/utils/redux';
import { addListPageItem, removeListPageItem, setListToast } from '@/utils/slices/exploreSlice';

const useCurrentListItemActions = (listId: number, belongsToCurrentUser: boolean) => {
  const dispatch = useAppDispatch();
  const user = useUser();
  const reduxListItems = useAppSelector((state: { lists: ListsState }) => state.lists.listItems);
  const listPageItems = useAppSelector(state => state.explore.listPageItems);
  const listToast = useAppSelector(state => state.explore.listToast);

  const addToCurrentList = useCallback(
    (type: ListItem['type'], objectId: number) => {
      const now = new Date().toISOString();
      dispatch(addListPageItem({ listId, type, objectId, metadata: { created: now, updated: now, status: 'active' } }));
      dispatch(addListItem({ userId: user!.id, type, objectId, listId, belongsToCurrentUser }));
      dispatch(setListToast({ type: 'added', itemType: type, objectId }));
    },
    [dispatch, user, listId, belongsToCurrentUser]
  );

  const removeFromCurrentList = useCallback(
    (type: ListItem['type'], objectId: number, itemId?: number) => {
      const id = itemId ?? reduxListItems?.[listId]?.[type]?.[objectId]?.id;
      const order = reduxListItems?.[listId]?.[type]?.[objectId]?.order;
      if (!id) {
        return;
      }
      const fullItem = listPageItems?.find(i => i.type === type && i.objectId === objectId);
      const removedItem: ListItem = fullItem ? { ...fullItem, id, order } : { listId, type, objectId, id, order };
      dispatch(removeListPageItem({ type, objectId }));
      dispatch(removeFromListItem({ userId: user!.id, type, itemId: id, listId, objectId, order, belongsToCurrentUser }));
      dispatch(setListToast({ type: 'removed', item: removedItem }));
    },
    [dispatch, user, listId, reduxListItems, belongsToCurrentUser, listPageItems]
  );

  const isInCurrentList = useCallback(
    (type: ListItem['type'], objectId: number) => !!reduxListItems?.[listId]?.[type]?.[objectId],
    [listId, reduxListItems]
  );

  const undoAdd = useCallback(() => {
    if (!listToast || listToast.type !== 'added') {
      return;
    }
    const { itemType, objectId } = listToast;
    const id = reduxListItems?.[listId]?.[itemType]?.[objectId]?.id;
    const order = reduxListItems?.[listId]?.[itemType]?.[objectId]?.order;
    dispatch(removeListPageItem({ type: itemType, objectId }));
    if (id) {
      dispatch(removeFromListItem({ userId: user!.id, type: itemType, itemId: id, listId, objectId, order, belongsToCurrentUser }));
    } else {
      dispatch(removeItemFromList({ listItem: { listId, type: itemType, objectId }, belongsToCurrentUser }));
    }
    dispatch(setListToast(null));
  }, [dispatch, listToast, user, listId, reduxListItems, belongsToCurrentUser]);

  const undoRemove = useCallback(() => {
    if (!listToast || listToast.type !== 'removed') {
      return;
    }
    const { item } = listToast;
    dispatch(addListPageItem(item));
    dispatch(addListItem({ userId: user!.id, type: item.type, objectId: item.objectId, listId, belongsToCurrentUser }));
    dispatch(setListToast(null));
  }, [dispatch, listToast, user, listId, belongsToCurrentUser]);

  return { addToCurrentList, isInCurrentList, removeFromCurrentList, undoAdd, undoRemove };
};

export default useCurrentListItemActions;
